'use client';

declare global {
    interface Window {
        FB: any;
    }
}

const FB_SDK_JAVASCRIPT_URL = 'https://connect.facebook.net/en_US/sdk.js';

export class FacebookService {
    private initializationPromise: Promise<void> | null = null;
    private isInitialized = false;

    initialize(appId: string): Promise<void> {
        // If already initialized, return resolved promise
        if (this.isInitialized && window.FB) {
            return Promise.resolve();
        }

        // If initialization is in progress, return the existing promise
        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        // If FB is already available but not marked as initialized, mark it
        if (window.FB) {
            this.isInitialized = true;
            return Promise.resolve();
        }

        // Start new initialization
        this.initializationPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = FB_SDK_JAVASCRIPT_URL;
            script.async = true;
            script.defer = true;
            script.onload = () => {
                try {
                window.FB?.init({
                    appId: appId,
                    autoLogAppEvents: true,
                    xfbml: true,
                    version: 'v17.0',
                });
                    this.isInitialized = true;
                    resolve();
                } catch (error) {
                    reject(error);
                }
            };
            script.onerror = () => {
                reject(new Error('Failed to load Facebook SDK'));
            };
            document.body.appendChild(script);
        });

        return this.initializationPromise;
    }

    private async waitForInitialization(): Promise<void> {
        if (this.isInitialized && window.FB) {
            return Promise.resolve();
        }

        // Wait for initialization with timeout
        if (this.initializationPromise) {
            return this.initializationPromise;
        }

        // If no initialization has started, wait for SDK to load with timeout
        return new Promise((resolve, reject) => {
            const maxWaitTime = 5000; // 5 seconds
            const startTime = Date.now();
            const checkInterval = setInterval(() => {
                if (window.FB) {
                    this.isInitialized = true;
                    clearInterval(checkInterval);
                    resolve();
                } else if (Date.now() - startTime > maxWaitTime) {
                    clearInterval(checkInterval);
                    reject(new Error('Facebook SDK initialization timeout'));
                }
            }, 100);
        });
    }

    async getProfileImageUrl(userToken: string): Promise<string> {
        // Wait for SDK to be ready
        await this.waitForInitialization();

        if (!window.FB) {
            throw new Error('Facebook SDK not initialized');
        }

            return new Promise((resolve, reject) => {
                window.FB.api('/me', {
                    fields: 'picture',
                    access_token: userToken,
                    height: 100,
                    width: 100,
                }, (response: any) => {
                if (response?.error) {
                    reject(new Error(response.error.message || 'Facebook API error'));
                } else if (response?.picture?.data?.url) {
                    resolve(response.picture.data.url);
                } else {
                    reject(new Error('Failed to get profile image URL'));
                }
            });
        });
    }

}

export default new FacebookService();